import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AUTH_SHORTCUT_POPUP_ANIMATION_MS, BrowserUrlConsts } from '@lib/core/service/consts';
import { prependBasename } from '@lib/core/service/utils';
import { localeWidget } from '@lib/tools/locale/source/web/widget';
import { EMAIL, GOOGLE } from '@lib/tools/shared/helpers/consts';
import { PAGES } from '@lib/tools/views/urls';

import { CloseButton } from '@components/web/src/atoms/Buttons/CloseButton/CloseButton';
import AuthorizationButton from '@components/web/src/atoms/Buttons/SocialButtons/AuthorizationButton/AuthorizationButton';
import { Flexbox } from '@components/web/src/foundations/Flexbox/Flexbox';
import { Text } from '@components/web/src/foundations/Text/Text';
import GoogleLoginButton from '@components/web/src/organisms/Authentication/GoogleLoginButton/GoogleLoginButton';
import { COLORS } from '@components/web/src/styles';
import * as S from '@components/web/src/templates/LoginAndRegistration/AuthShortcutPopup/styles';

interface Props {
  isOpen: boolean;
  isRegistrationVariant?: boolean;
  onClose: () => void;
}

export const AuthShortcutPopup = ({ isOpen, isRegistrationVariant = false, onClose }: Props) => {
  const navigate = useNavigate();
  const popupRef = useRef<HTMLDivElement>(null);
  const { googleBtn, emailBtn } = localeWidget.authenticationPage;
  const { signInTitle, registrationTitle } = localeWidget.authShortcutPopup;

  const [isMounted, setIsMounted] = useState(isOpen);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      setIsClosing(false);
      return;
    }

    if (!isMounted) {
      return;
    }

    setIsClosing(true);
    const timerId = setTimeout(() => {
      setIsMounted(false);
      setIsClosing(false);
    }, AUTH_SHORTCUT_POPUP_ANIMATION_MS);

    return () => clearTimeout(timerId);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Defer so the interaction that opened the popup does not immediately close it.
    const timerId = window.setTimeout(() => {
      document.addEventListener('pointerdown', handlePointerDown);
    }, 0);

    return () => {
      window.clearTimeout(timerId);
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen && !isMounted) {
    return null;
  }

  const redirectPath = isRegistrationVariant ? PAGES.vinhood.registration : PAGES.vinhood.signIn;

  const navigateToAuthPage = (authMethod: typeof EMAIL | typeof GOOGLE) => {
    onClose();
    navigate(
      prependBasename(redirectPath, {
        [BrowserUrlConsts.authMethod]: authMethod,
      }),
      { state: { shouldRedirectToHome: true } },
    );
  };

  const handleGoogleButtonClick = () => {
    navigateToAuthPage(GOOGLE);
  };

  const handleEmailButtonClick = () => {
    navigateToAuthPage(EMAIL);
  };

  return (
    <S.AuthShortcutPopupContainer
      ref={popupRef}
      isFullWidth
      $isClosing={isClosing}
      direction="column"
      gap={16}
      padding="16px 16px 32px"
    >
      <Flexbox gap={8} justify="space-between">
        <Text
          color={COLORS.base.black}
          fontType="grace"
          size="subtitle1"
          text={isRegistrationVariant ? registrationTitle : signInTitle}
          weight="semibold"
        />
        <CloseButton handleClick={onClose} />
      </Flexbox>
      <Flexbox direction="column" gap={8}>
        <GoogleLoginButton isPopupRedirect handleGoogleButtonClick={handleGoogleButtonClick} text={googleBtn} />

        <AuthorizationButton
          key={EMAIL}
          handleClick={handleEmailButtonClick}
          text={emailBtn}
          type={EMAIL}
          variant="secondary"
        />
      </Flexbox>
    </S.AuthShortcutPopupContainer>
  );
};
